import { formatExternalApiDateTime } from "@/Utile/ncrmExternalApi.mjs";
import {
  fetchSelfRegistrationIntakeOptions,
  fetchSelfRegistrationIntakeSettings,
  fetchSelfRegistrationIntakes,
  retrySelfRegistrationIntake,
  saveSelfRegistrationIntakeSettings,
} from "./adminWorkflowsApi.mjs";

export const DISPATCH_STATUS_COLORS = Object.freeze({
  pending: "info",
  configuration_required: "warning",
  queued: "info",
  dispatching: "info",
  succeeded: "success",
  failed_retryable: "warning",
  failed_terminal: "error",
});

export const DISPATCH_FILTER_STATUSES = Object.freeze([
  "pending",
  "configuration_required",
  "queued",
  "dispatching",
  "succeeded",
  "failed_retryable",
  "failed_terminal",
]);

// Statuses an admin may push back into the queue by hand; mirrors the backend
// allowlist so the button is never offered for a row the API would refuse.
export const RETRYABLE_DISPATCH_STATUSES = new Set([
  "pending",
  "configuration_required",
  "queued",
  "failed_retryable",
  "failed_terminal",
]);

export const defaultSettingsForm = () => ({
  targetCompanyId: null,
  externalApiEndpointId: null,
  enrichmentWorkflowInstanceId: null,
  apiKey: "",
  enabled: false,
});

export const buildSettingsPayload = (form) => ({
  targetCompanyId: form.targetCompanyId,
  externalApiEndpointId: form.externalApiEndpointId,
  enrichmentWorkflowInstanceId: form.enrichmentWorkflowInstanceId,
  enabled: Boolean(form.enabled),
  ...(form.apiKey ? { apiKey: form.apiKey } : {}),
});

export const settingsFormFromOverview = (overview) => {
  const settings = overview?.settings || null;
  return {
    ...defaultSettingsForm(),
    targetCompanyId: settings?.targetCompanyId ?? null,
    externalApiEndpointId: settings?.externalApiEndpointId ?? null,
    enrichmentWorkflowInstanceId: settings?.enrichmentWorkflowInstanceId ?? null,
    enabled: Boolean(settings?.enabled),
  };
};

export default {
  data() {
    return {
      loading: false,
      saving: false,
      retryingId: null,
      errorMessage: "",
      successMessage: "",
      overview: null,
      options: { companies: [], endpoints: [], workflows: [] },
      form: defaultSettingsForm(),
      intakes: [],
      summary: {},
      total: 0,
      page: 1,
      limit: 25,
      filters: { search: "", status: "" },
      selectedIntake: null,
      successTimer: null,
    };
  },
  computed: {
    hasStoredApiKey() {
      return Boolean(this.overview?.settings?.hasApiKey);
    },
    dispatchActive() {
      return Boolean(this.overview?.dispatchActive);
    },
    configurationMissing() {
      return !this.overview?.configured;
    },
    dispatchFlagDisabled() {
      return this.overview != null && !this.overview.dispatchFlagEnabled;
    },
    canSave() {
      if (this.saving) return false;
      if (!this.form.targetCompanyId) return false;
      if (!this.form.externalApiEndpointId) return false;
      if (!this.form.enrichmentWorkflowInstanceId) return false;
      return Boolean(this.form.apiKey) || this.hasStoredApiKey;
    },
    companyItems() {
      return (this.options.companies || []).map((company) => ({
        value: Number(company.id),
        label: company.name,
      }));
    },
    endpointItems() {
      return (this.options.endpoints || []).map((endpoint) => ({
        value: Number(endpoint.id),
        label: `${endpoint.name} (${endpoint.status})`,
      }));
    },
    workflowItems() {
      return (this.options.workflows || []).map((workflow) => ({
        value: workflow.id,
        label: `${workflow.name} (${workflow.status})`,
      }));
    },
    statusFilterItems() {
      return [
        { value: "", label: this.$t("admin.workflows.selfRegistrationIntake.filters.allStatuses") },
        ...DISPATCH_FILTER_STATUSES.map((value) => ({ value, label: this.statusLabel(value) })),
      ];
    },
    kpiItems() {
      return [
        {
          key: "total",
          label: this.$t("admin.workflows.selfRegistrationIntake.metrics.total"),
          value: Number(this.summary.total || 0),
          icon: "mdi-account-multiple-outline",
          color: "primary",
        },
        {
          key: "succeeded",
          label: this.$t("admin.workflows.selfRegistrationIntake.metrics.succeeded"),
          value: Number(this.summary.succeeded || 0),
          icon: "mdi-check-circle-outline",
          color: "success",
        },
        {
          key: "waiting",
          label: this.$t("admin.workflows.selfRegistrationIntake.metrics.waiting"),
          value:
            Number(this.summary.pending || 0) +
            Number(this.summary.queued || 0) +
            Number(this.summary.dispatching || 0),
          icon: "mdi-timer-sand",
          color: "info",
        },
        {
          key: "attention",
          label: this.$t("admin.workflows.selfRegistrationIntake.metrics.attention"),
          value:
            Number(this.summary.configuration_required || 0) +
            Number(this.summary.failed_retryable || 0) +
            Number(this.summary.failed_terminal || 0),
          icon: "mdi-alert-circle-outline",
          color: "warning",
        },
      ];
    },
    headers() {
      const t = (key) => this.$t(`admin.workflows.selfRegistrationIntake.table.${key}`);
      return [
        { title: t("contact"), key: "contact", sortable: false },
        { title: t("company"), key: "company", sortable: false },
        { title: t("product"), key: "product", sortable: false },
        { title: t("status"), key: "status", sortable: false },
        { title: t("attempts"), key: "attempts", sortable: false },
        { title: t("createdAt"), key: "createdAt", sortable: false },
        { title: "", key: "action", sortable: false, width: 48 },
      ];
    },
    intakeRows() {
      return this.intakes.map((intake) => ({
        id: intake.id,
        raw: intake,
        contact: [intake.registration?.first_name, intake.registration?.last_name]
          .filter(Boolean)
          .join(" "),
        email: intake.registration?.email || "",
        company: intake.registration?.company_name || intake.tenant?.companyName || "",
        product: intake.registration?.product_key || "",
        status: intake.dispatch?.status || "pending",
        attempts: Number(intake.dispatch?.attemptCount || 0),
        createdAt: this.formatDate(intake.createdAt),
        canRetry: RETRYABLE_DISPATCH_STATUSES.has(intake.dispatch?.status),
      }));
    },
    pageCount() {
      return Math.max(1, Math.ceil(Number(this.total || 0) / Number(this.limit || 25)));
    },
  },
  mounted() {
    this.loadWorkspace();
  },
  beforeUnmount() {
    if (this.successTimer) clearTimeout(this.successTimer);
  },
  methods: {
    // Non-intrusive confirmation with a short timeout, per docs/design.md.
    flashSuccess(message) {
      this.successMessage = message;
      if (this.successTimer) clearTimeout(this.successTimer);
      this.successTimer = setTimeout(() => {
        this.successMessage = "";
        this.successTimer = null;
      }, 4000);
    },
    statusLabel(status) {
      return this.$t(`admin.workflows.selfRegistrationIntake.statuses.${status}`);
    },
    statusColor(status) {
      return DISPATCH_STATUS_COLORS[status] || "secondary";
    },
    productLabel(productKey) {
      if (!productKey) return "";
      return this.$t(`admin.workflows.selfRegistrationIntake.products.${productKey}`);
    },
    formatDate(value) {
      return formatExternalApiDateTime(value);
    },
    async loadWorkspace() {
      this.loading = true;
      this.errorMessage = "";
      try {
        const { data } = await fetchSelfRegistrationIntakeSettings();
        this.overview = data;
        this.form = settingsFormFromOverview(data);
        await this.loadOptions(this.form.targetCompanyId);
        await this.loadIntakes();
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.error || this.$t("admin.workflows.errors.load");
      } finally {
        this.loading = false;
      }
    },
    async loadOptions(companyId) {
      const { data } = await fetchSelfRegistrationIntakeOptions(companyId || null);
      this.options = {
        companies: data?.companies || [],
        endpoints: data?.endpoints || [],
        workflows: data?.workflows || [],
      };
    },
    async handleTargetCompanyChange(companyId) {
      this.form.targetCompanyId = companyId || null;
      this.form.externalApiEndpointId = null;
      this.form.enrichmentWorkflowInstanceId = null;
      // A new target company invalidates the stored key, so ask for it again.
      this.form.apiKey = "";
      try {
        await this.loadOptions(companyId);
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.error || this.$t("admin.workflows.errors.load");
      }
    },
    async loadIntakes() {
      const { data } = await fetchSelfRegistrationIntakes({
        page: this.page,
        limit: this.limit,
        ...(this.filters.search ? { search: this.filters.search } : {}),
        ...(this.filters.status ? { status: this.filters.status } : {}),
      });
      this.intakes = Array.isArray(data?.items) ? data.items : [];
      this.summary = data?.summary || {};
      this.total = Number(data?.total || 0);
    },
    async applyFilters() {
      this.page = 1;
      await this.reloadIntakes();
    },
    async changePage(page) {
      this.page = page;
      await this.reloadIntakes();
    },
    async reloadIntakes() {
      this.loading = true;
      this.errorMessage = "";
      try {
        await this.loadIntakes();
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.error || this.$t("admin.workflows.errors.load");
      } finally {
        this.loading = false;
      }
    },
    async saveSettings() {
      this.saving = true;
      this.errorMessage = "";
      this.successMessage = "";
      try {
        const { data } = await saveSelfRegistrationIntakeSettings(buildSettingsPayload(this.form));
        this.overview = data;
        this.form = settingsFormFromOverview(data);
        this.flashSuccess(this.$t("admin.workflows.selfRegistrationIntake.messages.saved"));
        await this.loadIntakes();
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.error || this.$t("admin.workflows.errors.save");
      } finally {
        this.saving = false;
      }
    },
    async retryIntake(intakeId) {
      this.retryingId = intakeId;
      this.errorMessage = "";
      this.successMessage = "";
      try {
        const { data } = await retrySelfRegistrationIntake(intakeId);
        this.flashSuccess(
          data?.dispatched
            ? this.$t("admin.workflows.selfRegistrationIntake.messages.retrySucceeded")
            : this.$t("admin.workflows.selfRegistrationIntake.messages.retryQueued")
        );
        if (this.selectedIntake?.id === intakeId && data?.intake) {
          this.selectedIntake = data.intake;
        }
        await this.loadIntakes();
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.error ||
          this.$t("admin.workflows.selfRegistrationIntake.errors.retry");
      } finally {
        this.retryingId = null;
      }
    },
    openIntake(intake) {
      this.selectedIntake = intake || null;
    },
    closeIntake() {
      this.selectedIntake = null;
    },
  },
};
